import type { ObjectView, ZoneView as Zone } from '../model/table';
import { ObjectCard } from './ObjectCard';

export interface ZoneDisplay {
  display?: 'auto' | 'row' | 'grid' | 'fan' | 'stack' | 'list';
  columns?: number;
  size?: 's' | 'm' | 'l';
  showEmpty?: boolean;
}

interface Props extends ZoneDisplay {
  zone: Zone;
  selectable: Set<string>;
  inspected: string | null;
  onInspect: (id: string) => void;
  seatIndex: Map<string, number>;
  attached: Map<string, ObjectView[]>;
  compact?: boolean;
  large?: boolean;
  bare?: boolean;
}

const AUTO_DISPLAY: Record<string, NonNullable<ZoneDisplay['display']>> = {
  hand: 'fan', grid: 'grid', stack: 'stack', discard: 'stack', row: 'row', area: 'row', track: 'row', supply: 'row', board: 'row',
};

export function ZoneView({ zone, selectable, inspected, onInspect, seatIndex, attached, compact, large, bare, display = 'auto', columns, size, showEmpty = true }: Props) {
  const hiddenCount = zone.count !== null ? zone.count - zone.objects.length : 0;
  const cardSize = size === 's' || compact ? 'compact' : size === 'l' || large ? 'large' : 'normal';
  const mode = display === 'auto' ? AUTO_DISPLAY[zone.kind] ?? 'row' : display;
  const countText = zone.count !== null ? `${zone.count}` : `${zone.objects.length}`;
  const empty = zone.objects.length === 0 && hiddenCount <= 0;
  if (empty && !showEmpty) return null;
  // Objects attached to another object in this zone are drawn on their host, not again here.
  const hosted = new Set(zone.objects.filter(o => o.stack && zone.objects.some(h => h.id === o.stack)).map(o => o.id));
  const style = mode === 'grid' && columns ? ({ gridTemplateColumns: `repeat(${columns}, max-content)` } as React.CSSProperties) : undefined;
  return (
    <section className={`zone zone-${zone.kind}${compact ? ' zone-compact' : ''}${bare ? ' zone-bare' : ''}`} aria-label={`${zone.label}, ${countText} items`}>
      <h3 className={bare ? "zone-title visually-hidden" : "zone-title"}>{zone.label}<span className="zone-count">{countText}</span></h3>
      {empty ? (
        <p className="zone-empty">Empty</p>
      ) : mode === 'list' ? (
        <ul className="zone-list">
          {zone.objects.filter(o => !hosted.has(o.id)).map(o => (
            <li key={o.id}><button type="button" className={`zone-list-item${selectable.has(o.id) ? ' is-selectable' : ''}${inspected === o.id ? ' is-selected' : ''}`} onClick={() => onInspect(o.id)} aria-label={o.label}>{o.title}</button></li>
          ))}
        </ul>
      ) : (
        <div className={`zone-items zone-items-${mode}`} style={style}>
          {zone.objects.filter(o => !hosted.has(o.id)).map(o => (
            <ObjectCard key={o.id} object={o} zoneKind={zone.kind} size={cardSize} selectable={selectable.has(o.id)} selected={inspected === o.id}
              seatIndex={seatIndex} attached={attached.get(o.id)} onInspect={onInspect} />
          ))}
          {hiddenCount > 0 && (
            <span className="object object-card object-hidden" role="img" aria-label={`${hiddenCount} hidden`}>
              <span className="object-back" aria-hidden="true" /><span className="object-count">×{hiddenCount}</span>
            </span>
          )}
        </div>
      )}
    </section>
  );
}
